/**
 * useAICompose Hook
 *
 * AI compose, smart replies, tone adjust, subject suggest via React Query mutations.
 */

import { useState, useCallback } from 'react';
import { api, EmailAIReplySuggestion } from '@/lib/api';

export function useAICompose(accountId: string | undefined) {
  const [composing, setComposing] = useState(false);
  const [replySuggestions, setReplySuggestions] = useState<EmailAIReplySuggestion[]>([]);
  const [suggestingReplies, setSuggestingReplies] = useState(false);
  const [adjustingTone, setAdjustingTone] = useState(false);
  const [suggestingSubject, setSuggestingSubject] = useState(false);
  const [subjectSuggestions, setSubjectSuggestions] = useState<string[]>([]);

  const aiCompose = useCallback(
    async (
      prompt: string,
      context?: {
        replyToMessageId?: string;
        tone?: 'formal' | 'casual' | 'friendly' | 'professional';
        length?: 'short' | 'medium' | 'long';
      },
    ) => {
      if (!accountId) return null;
      setComposing(true);
      try {
        const result = await api.aiComposeEmail({
          accountId,
          prompt,
          context,
        });
        return result;
      } finally {
        setComposing(false);
      }
    },
    [accountId],
  );

  const fetchReplySuggestions = useCallback(
    async (messageId: string) => {
      if (!accountId) return;
      setSuggestingReplies(true);
      try {
        const result = await api.aiEmailReplySuggestions(accountId, messageId);
        setReplySuggestions(result.suggestions);
      } catch {
        setReplySuggestions([]);
      } finally {
        setSuggestingReplies(false);
      }
    },
    [accountId],
  );

  const adjustTone = useCallback(
    async (
      bodyHtml: string,
      targetTone: 'formal' | 'casual' | 'diplomatic' | 'assertive' | 'friendly',
    ) => {
      if (!accountId) return null;
      setAdjustingTone(true);
      try {
        const result = await api.aiAdjustEmailTone(accountId, bodyHtml, targetTone);
        return result.bodyHtml;
      } finally {
        setAdjustingTone(false);
      }
    },
    [accountId],
  );

  const suggestSubjects = useCallback(
    async (bodyText: string) => {
      if (!accountId) return;
      setSuggestingSubject(true);
      try {
        const result = await api.aiSuggestEmailSubject(accountId, bodyText);
        setSubjectSuggestions(result.suggestions);
      } catch {
        setSubjectSuggestions([]);
      } finally {
        setSuggestingSubject(false);
      }
    },
    [accountId],
  );

  const clearReplySuggestions = useCallback(() => setReplySuggestions([]), []);
  const clearSubjectSuggestions = useCallback(() => setSubjectSuggestions([]), []);

  return {
    composing,
    aiCompose,
    replySuggestions,
    suggestingReplies,
    fetchReplySuggestions,
    clearReplySuggestions,
    adjustingTone,
    adjustTone,
    suggestingSubject,
    subjectSuggestions,
    suggestSubjects,
    clearSubjectSuggestions,
  };
}
